import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  CheckCircle2Icon,
  ClipboardList,
  HeartHandshake,
  LoaderIcon,
  MoreVerticalIcon,
  PlusIcon,
  Users,
} from "lucide-react";

import { agencyDashboardApi } from "@/api/agencies";
import { ChartAreaInteractive } from "@/components/chart-area-interactive";
import { SectionCards } from "@/components/section-cards";
import { useAuth } from "@/auth/useAuth";
import { Alert } from "@/components/Alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { VisitModal } from "@/features/scheduling/VisitModal";

import { DashboardSkeleton } from "./components/DashboardSkeleton";

export function AgencyDashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showVisitModal, setShowVisitModal] = useState(false);

  const loadMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await agencyDashboardApi.getMetrics();
      setMetrics(data);
    } catch (err) {
      setError(err.message || "Failed to load dashboard metrics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, []);

  if (loading && !metrics) {
    return <DashboardSkeleton />;
  }

  const todaysVisits = metrics?.todays_visits?.list || [];
  const allVisits = metrics?.all_visits_list || [];
  const unassignedCount = metrics?.todays_visits?.unassigned_count || 0;
  const completionRate = metrics?.todays_visits?.completion_rate_percentage || 0;

  const series = [
    { date: metrics?.today_date, logins: 0, care_shifts: metrics?.todays_visits?.total || 0 },
  ];

  const cards = [
    {
      title: "Active Clients",
      value: String(metrics?.active_clients_count ?? 0),
      badge: "Active",
      trend: "up",
      footer: "Enrolled in active care",
      hint: "Clients currently receiving services",
      icon: Users,
      tone: "rose",
    },
    {
      title: "Caregivers",
      value: String(metrics?.active_caregivers_count ?? 0),
      badge: "Roster",
      trend: "up",
      footer: "On active staff roster",
      hint: "Available for visit assignment",
      icon: HeartHandshake,
      tone: "emerald",
    },
    {
      title: "Today's Visits",
      value: String(metrics?.todays_visits?.total ?? 0),
      badge: `${completionRate}% done`,
      trend: completionRate >= 50 ? "up" : "down",
      footer: `${metrics?.todays_visits?.completed || 0} completed today`,
      hint: `${unassignedCount} unassigned · ${metrics?.todays_visits?.in_progress || 0} in progress`,
      icon: CalendarDays,
      tone: "cyan",
    },
    {
      title: "Care Plans",
      value: String(metrics?.active_care_plans_count ?? 0),
      badge: `${metrics?.care_plan_categories?.length || 0} cats`,
      trend: "up",
      footer: "Active care plans",
      hint: `Agency: ${metrics?.agency_name || "—"}`,
      icon: ClipboardList,
      tone: "amber",
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Organization Dashboard"
        description={
          user?.agency?.name
            ? `${user.agency.name} — clients, caregivers, and today's visits at a glance.`
            : "Clients, caregivers, and today's visits at a glance."
        }
      />

      {(error || unassignedCount > 0) && (
        <PageBody className="gap-3">
          {error ? (
            <Alert type="error" title="Could not load dashboard">
              {error}
            </Alert>
          ) : null}

          {unassignedCount > 0 ? (
            <Alert type="info" title="Unassigned visits">
              <span className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span>
                  {unassignedCount} shift{unassignedCount > 1 ? "s" : ""} today need a
                  caregiver.
                </span>
                <Button asChild size="sm" variant="outline" className="shrink-0">
                  <Link to="/schedule">Assign caregivers</Link>
                </Button>
              </span>
            </Alert>
          ) : null}
        </PageBody>
      )}

      <SectionCards cards={cards} />

      <PageBody className="gap-6">
        <ChartAreaInteractive
          series={series}
          days={7}
          title="Today's Care Volume"
          description={`${user?.agency?.name || "Agency"} · ${metrics?.today_date || "today"}`}
        />

        <Tabs defaultValue="today" className="flex w-full flex-col justify-start gap-6">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="today">
                Today{" "}
                <Badge
                  variant="secondary"
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-muted-foreground/30"
                >
                  {todaysVisits.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="all">
                All Shifts{" "}
                <Badge
                  variant="secondary"
                  className="flex h-5 w-5 items-center justify-center rounded-full bg-muted-foreground/30"
                >
                  {allVisits.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="plans">Care Plans</TabsTrigger>
            </TabsList>
            <Button size="sm" onClick={() => setShowVisitModal(true)}>
              <PlusIcon />
              <span className="hidden lg:inline">Schedule Visit</span>
            </Button>
          </div>

          <TabsContent value="today">
            <VisitTable visits={todaysVisits} empty="No visits scheduled for today." />
          </TabsContent>
          <TabsContent value="all">
            <VisitTable visits={allVisits} empty="No visits found." />
          </TabsContent>
          <TabsContent value="plans">
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Active plans</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!metrics?.care_plan_categories?.length ? (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        className="h-24 text-center text-muted-foreground"
                      >
                        No active care plans.
                      </TableCell>
                    </TableRow>
                  ) : (
                    metrics.care_plan_categories.map((cat) => (
                      <TableRow key={cat.category}>
                        <TableCell className="font-medium">{cat.label}</TableCell>
                        <TableCell>{cat.count}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </PageBody>

      {showVisitModal && (
        <VisitModal
          onClose={() => setShowVisitModal(false)}
          onSaved={() => {
            setShowVisitModal(false);
            loadMetrics();
          }}
        />
      )}
    </PageShell>
  );
}

function VisitTable({ visits, empty }) {
  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader className="bg-muted sticky top-0 z-10">
          <TableRow>
            <TableHead>Time</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Caregiver</TableHead>
            <TableHead>Service</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {visits.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                {empty}
              </TableCell>
            </TableRow>
          ) : (
            visits.map((v) => {
              const startStr = new Date(v.start_time).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              });
              const endStr = new Date(v.end_time).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              });
              const done = v.status === "completed";
              return (
                <TableRow key={v.id}>
                  <TableCell className="font-medium">
                    {startStr} – {endStr}
                  </TableCell>
                  <TableCell>
                    <Link to={`/clients/${v.client}`} className="no-underline hover:underline">
                      {v.client_name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {v.caregiver_name || (
                      <span className="text-destructive font-medium">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell>{v.service_type}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="flex w-fit gap-1 px-1.5 text-muted-foreground [&_svg]:size-3"
                    >
                      {done ? (
                        <CheckCircle2Icon className="text-emerald-600" />
                      ) : (
                        <LoaderIcon />
                      )}
                      {v.status.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-muted-foreground"
                        >
                          <MoreVerticalIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/visits/${v.id}`}>Open workspace</Link>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
